import {
  Search,
  Eye,
  RotateCcw,
  DollarSign,
  Package,
  User,
  FileText,
} from 'lucide-react'
import { useState } from 'react'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'

interface SaleItem {
  id: number
  productName: string
  quantity: number
  price: number
  discount: number
  subtotal: number
  returned: number
}

interface Sale {
  id: string
  date: string
  time: string
  customer: string
  seller: string
  deliveryPerson?: string
  branch: string
  items: SaleItem[]
  subtotal: number
  tax: number
  total: number
  paymentMethod: string
  invoiced: boolean
  status: 'completed' | 'partial_return' | 'total_return'
  returnReason?: string
  returnDate?: string
}

const sales: Sale[] = [
  {
    id: 'TC-1245',
    date: '2026-04-27',
    time: '14:32',
    customer: 'María López García',
    seller: 'Ana Torres',
    branch: 'Sucursal Centro',
    items: [
      {
        id: 1,
        productName: 'Paracetamol 500mg',
        quantity: 2,
        price: 45,
        discount: 0,
        subtotal: 90,
        returned: 0,
      },
      {
        id: 2,
        productName: 'Ibuprofeno 400mg',
        quantity: 1,
        price: 65,
        discount: 10,
        subtotal: 58.5,
        returned: 0,
      },
      {
        id: 3,
        productName: 'Loratadina 10mg',
        quantity: 3,
        price: 120,
        discount: 0,
        subtotal: 360,
        returned: 0,
      },
    ],
    subtotal: 508.5,
    tax: 81.36,
    total: 589.86,
    paymentMethod: 'Tarjeta',
    invoiced: true,
    status: 'completed',
  },
  {
    id: 'EF-1244',
    date: '2026-04-27',
    time: '12:15',
    customer: 'Carlos Méndez',
    seller: 'María López',
    branch: 'Sucursal Norte',
    items: [
      {
        id: 1,
        productName: 'Amoxicilina 500mg',
        quantity: 1,
        price: 180,
        discount: 0,
        subtotal: 180,
        returned: 0,
      },
      {
        id: 2,
        productName: 'Omeprazol 20mg',
        quantity: 2,
        price: 95,
        discount: 0,
        subtotal: 190,
        returned: 0,
      },
    ],
    subtotal: 370,
    tax: 59.2,
    total: 429.2,
    paymentMethod: 'Efectivo',
    invoiced: false,
    status: 'completed',
  },
  {
    id: 'EF-1242',
    date: '2026-04-26',
    time: '16:45',
    customer: 'Patricia Silva',
    seller: 'Carlos Méndez',
    deliveryPerson: 'Juan Pérez',
    branch: 'Sucursal Sur',
    items: [
      {
        id: 1,
        productName: 'Paracetamol 500mg',
        quantity: 3,
        price: 45,
        discount: 0,
        subtotal: 135,
        returned: 1,
      },
      {
        id: 2,
        productName: 'Complejo B',
        quantity: 2,
        price: 110,
        discount: 5,
        subtotal: 209,
        returned: 0,
      },
    ],
    subtotal: 344,
    tax: 55.04,
    total: 399.04,
    paymentMethod: 'Efectivo',
    invoiced: false,
    status: 'partial_return',
    returnReason: 'Producto en mal estado',
    returnDate: '2026-04-27',
  },
  {
    id: 'BBVA-1238',
    date: '2026-04-26',
    time: '10:20',
    customer: 'Luis Ramírez',
    seller: 'Ana Torres',
    branch: 'Sucursal Centro',
    items: [
      {
        id: 1,
        productName: 'Losartán 50mg',
        quantity: 1,
        price: 150,
        discount: 0,
        subtotal: 150,
        returned: 1,
      },
      {
        id: 2,
        productName: 'Metformina 850mg',
        quantity: 1,
        price: 85,
        discount: 0,
        subtotal: 85,
        returned: 1,
      },
    ],
    subtotal: 235,
    tax: 37.6,
    total: 272.6,
    paymentMethod: 'Transferencia',
    invoiced: true,
    status: 'total_return',
    returnReason: 'Cliente no requiere los productos',
    returnDate: '2026-04-26',
  },
  {
    id: 'MP-1156',
    date: '2026-04-27',
    time: '09:30',
    customer: 'Sofía Martínez',
    seller: 'María López',
    deliveryPerson: 'Roberto López',
    branch: 'Sucursal Norte',
    items: [
      {
        id: 1,
        productName: 'Vitamina C 1000mg',
        quantity: 2,
        price: 95,
        discount: 0,
        subtotal: 190,
        returned: 0,
      },
    ],
    subtotal: 190,
    tax: 30.4,
    total: 220.4,
    paymentMethod: 'Transferencia',
    invoiced: false,
    status: 'completed',
  },
]

export function Sales() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedBranch, setSelectedBranch] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedSeller, setSelectedSeller] = useState('all')
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState('all')
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('all')
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState('all')
  const [startDate, setStartDate] = useState('2026-04-01')
  const [endDate, setEndDate] = useState('2026-04-27')
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showReturnModal, setShowReturnModal] = useState(false)
  const [returnType, setReturnType] = useState<'partial' | 'total'>('partial')
  const [returnReason, setReturnReason] = useState('')
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [itemQuantities, setItemQuantities] = useState<Record<number, number>>(
    {},
  )

  const branches = ['all', ...Array.from(new Set(sales.map((s) => s.branch)))]
  const sellers = ['all', ...Array.from(new Set(sales.map((s) => s.seller)))]
  const deliveryPersons = [
    'all',
    ...Array.from(
      new Set(
        sales.filter((s) => s.deliveryPerson).map((s) => s.deliveryPerson!),
      ),
    ),
  ]
  const paymentMethods = [
    'all',
    ...Array.from(new Set(sales.map((s) => s.paymentMethod))),
  ]

  const filteredSales = sales.filter((sale) => {
    const matchesSearch =
      sale.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.items.some((item) =>
        item.productName.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    const matchesBranch =
      selectedBranch === 'all' || sale.branch === selectedBranch
    const matchesStatus =
      selectedStatus === 'all' || sale.status === selectedStatus
    const matchesSeller =
      selectedSeller === 'all' || sale.seller === selectedSeller
    const matchesDeliveryPerson =
      selectedDeliveryPerson === 'all' ||
      sale.deliveryPerson === selectedDeliveryPerson
    const matchesPaymentMethod =
      selectedPaymentMethod === 'all' ||
      sale.paymentMethod === selectedPaymentMethod
    const matchesInvoiceStatus =
      selectedInvoiceStatus === 'all' ||
      (selectedInvoiceStatus === 'invoiced' && sale.invoiced) ||
      (selectedInvoiceStatus === 'not_invoiced' && !sale.invoiced)
    return (
      matchesSearch &&
      matchesBranch &&
      matchesStatus &&
      matchesSeller &&
      matchesDeliveryPerson &&
      matchesPaymentMethod &&
      matchesInvoiceStatus
    )
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700'
      case 'partial_return':
        return 'bg-yellow-100 text-yellow-700'
      case 'total_return':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-slate-100 text-slate-700'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completada'
      case 'partial_return':
        return 'Devolución Parcial'
      case 'total_return':
        return 'Devolución Total'
      default:
        return status
    }
  }

  const openDetailModal = (sale: Sale) => {
    setSelectedSale(sale)
    setShowDetailModal(true)
  }

  const openReturnModal = (sale: Sale, type: 'partial' | 'total') => {
    setSelectedSale(sale)
    setReturnType(type)
    setSelectedItems([])
    setItemQuantities({})
    setReturnReason('')
    setShowReturnModal(true)
  }

  const toggleItemSelection = (itemId: number, maxQuantity: number) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter((id) => id !== itemId))
      const newQuantities = { ...itemQuantities }
      delete newQuantities[itemId]
      setItemQuantities(newQuantities)
    } else {
      setSelectedItems([...selectedItems, itemId])
      setItemQuantities({ ...itemQuantities, [itemId]: maxQuantity })
    }
  }

  const updateItemQuantity = (itemId: number, quantity: number) => {
    setItemQuantities({ ...itemQuantities, [itemId]: quantity })
  }

  const processReturn = () => {
    console.log('Procesando devolución:', {
      sale: selectedSale?.id,
      type: returnType,
      reason: returnReason,
      items:
        returnType === 'partial'
          ? selectedItems.map((id) => ({
              id,
              quantity: itemQuantities[id],
            }))
          : 'todos',
    })
    setShowReturnModal(false)
    setShowDetailModal(false)
  }

  const calculateReturnTotal = () => {
    if (!selectedSale) return 0
    if (returnType === 'total') return selectedSale.total

    return (
      selectedSale.items
        .filter((item) => selectedItems.includes(item.id))
        .reduce((sum, item) => {
          const quantity = itemQuantities[item.id] || 0
          const itemPrice = item.price * (1 - item.discount / 100)
          return sum + itemPrice * quantity
        }, 0) * 1.16
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Registro de Ventas
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {filteredSales.length} ventas registradas
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-50 p-2">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Ventas del Día</p>
              <p className="text-2xl font-semibold text-slate-900">$1,290.70</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-50 p-2">
              <Package className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Transacciones</p>
              <p className="text-2xl font-semibold text-slate-900">4</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-yellow-50 p-2">
              <RotateCcw className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Devoluciones</p>
              <p className="text-2xl font-semibold text-slate-900">2</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-50 p-2">
              <User className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Ticket Promedio</p>
              <p className="text-2xl font-semibold text-slate-900">$422.65</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar ticket, cliente o medicamento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger>
              <SelectValue placeholder="Sucursal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las sucursales</SelectItem>
              {branches
                .filter((b) => b !== 'all')
                .map((branch) => (
                  <SelectItem key={branch} value={branch}>
                    {branch}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger>
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="completed">Completadas</SelectItem>
              <SelectItem value="partial_return">Devolución Parcial</SelectItem>
              <SelectItem value="total_return">Devolución Total</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedSeller} onValueChange={setSelectedSeller}>
            <SelectTrigger>
              <SelectValue placeholder="Vendedor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los vendedores</SelectItem>
              {sellers
                .filter((s) => s !== 'all')
                .map((seller) => (
                  <SelectItem key={seller} value={seller}>
                    {seller}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Select
            value={selectedDeliveryPerson}
            onValueChange={setSelectedDeliveryPerson}
          >
            <SelectTrigger>
              <SelectValue placeholder="Repartidor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los repartidores</SelectItem>
              {deliveryPersons
                .filter((d) => d !== 'all')
                .map((person) => (
                  <SelectItem key={person} value={person}>
                    {person}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          <Select
            value={selectedPaymentMethod}
            onValueChange={setSelectedPaymentMethod}
          >
            <SelectTrigger>
              <SelectValue placeholder="Método de pago" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los métodos</SelectItem>
              {paymentMethods
                .filter((p) => p !== 'all')
                .map((method) => (
                  <SelectItem key={method} value={method}>
                    {method}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          <Select
            value={selectedInvoiceStatus}
            onValueChange={setSelectedInvoiceStatus}
          >
            <SelectTrigger>
              <SelectValue placeholder="Estado de facturación" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="invoiced">Facturadas</SelectItem>
              <SelectItem value="not_invoiced">Sin facturar</SelectItem>
            </SelectContent>
          </Select>

          <div className="grid grid-cols-2 gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Sales Table */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                  Ticket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                  Fecha y Hora
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                  Vendedor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                  Facturación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredSales.map((sale) => (
                <tr key={sale.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-medium text-slate-900">
                    {sale.id}
                  </td>
                  <td className="px-6 py-4 text-slate-700">
                    <div>{sale.date}</div>
                    <div className="text-xs text-slate-500">{sale.time}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-700">
                    <div>{sale.customer}</div>
                    {sale.deliveryPerson && (
                      <div className="mt-1 text-xs text-blue-600">
                        Repartidor: {sale.deliveryPerson}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-700">{sale.seller}</td>
                  <td className="px-6 py-4 font-semibold text-slate-900">
                    ${sale.total.toFixed(2)}
                  </td>
                  <td className="px-6 py-4">
                    {sale.invoiced ? (
                      <Badge className="bg-green-100 text-green-700">
                        Facturada
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-700">
                        Sin facturar
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <Badge className={getStatusColor(sale.status)}>
                      {getStatusLabel(sale.status)}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openDetailModal(sale)}
                        className="rounded p-1 hover:bg-slate-100"
                        title="Ver detalle"
                      >
                        <Eye className="h-4 w-4 text-slate-600" />
                      </button>
                      {!sale.invoiced && sale.status === 'completed' && (
                        <button
                          onClick={() =>
                            window.open(
                              'https://factura.com/nueva-factura',
                              '_blank',
                            )
                          }
                          className="rounded p-1 hover:bg-slate-100"
                          title="Facturar venta"
                        >
                          <FileText className="h-4 w-4 text-blue-600" />
                        </button>
                      )}
                      {sale.status === 'completed' && (
                        <button
                          onClick={() => openReturnModal(sale, 'partial')}
                          className="rounded p-1 hover:bg-slate-100"
                          title="Procesar devolución"
                        >
                          <RotateCcw className="h-4 w-4 text-orange-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Detalle de Venta</DialogTitle>
            <DialogDescription className="sr-only">
              Información completa de la venta y productos vendidos
            </DialogDescription>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-6">
              {/* Sale Info */}
              <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-50 p-4">
                <div>
                  <p className="text-sm text-slate-600">Ticket:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedSale.id}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Fecha y Hora:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedSale.date} {selectedSale.time}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Cliente:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedSale.customer}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Vendedor:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedSale.seller}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Sucursal:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedSale.branch}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Método de Pago:</p>
                  <p className="font-semibold text-slate-900">
                    {selectedSale.paymentMethod}
                  </p>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="mb-3 font-medium text-slate-900">Productos</h4>
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Producto
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Cantidad
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Precio
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Desc.
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Subtotal
                        </th>
                        {selectedSale.status !== 'completed' && (
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                            Devuelto
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {selectedSale.items.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-2">{item.productName}</td>
                          <td className="px-4 py-2">{item.quantity}</td>
                          <td className="px-4 py-2">
                            ${item.price.toFixed(2)}
                          </td>
                          <td className="px-4 py-2">{item.discount}%</td>
                          <td className="px-4 py-2 font-medium">
                            ${item.subtotal.toFixed(2)}
                          </td>
                          {selectedSale.status !== 'completed' && (
                            <td className="px-4 py-2">
                              {item.returned > 0 && (
                                <Badge className="bg-red-100 text-red-700">
                                  {item.returned}
                                </Badge>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="space-y-2 border-t border-slate-200 pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-medium">
                    ${selectedSale.subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">IVA (16%):</span>
                  <span className="font-medium">
                    ${selectedSale.tax.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span className="font-semibold text-slate-900">Total:</span>
                  <span className="text-xl font-semibold text-slate-900">
                    ${selectedSale.total.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Return Info */}
              {selectedSale.status !== 'completed' && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                  <p className="text-sm font-medium text-yellow-900">
                    Información de Devolución
                  </p>
                  <p className="mt-1 text-sm text-yellow-800">
                    Fecha: {selectedSale.returnDate}
                  </p>
                  <p className="text-sm text-yellow-800">
                    Motivo: {selectedSale.returnReason}
                  </p>
                </div>
              )}

              {/* Actions */}
              {selectedSale.status === 'completed' && (
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowDetailModal(false)
                      openReturnModal(selectedSale, 'partial')
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Devolución Parcial
                  </Button>
                  <Button
                    onClick={() => {
                      setShowDetailModal(false)
                      openReturnModal(selectedSale, 'total')
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Devolución Total
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Return Modal */}
      <Dialog open={showReturnModal} onOpenChange={setShowReturnModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {returnType === 'partial'
                ? 'Devolución Parcial'
                : 'Devolución Total'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Procesar devolución de productos vendidos
            </DialogDescription>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-6">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-900">
                  Ticket: {selectedSale.id}
                </p>
                <p className="text-sm text-slate-600">
                  Cliente: {selectedSale.customer}
                </p>
              </div>

              {/* Return Type Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Tipo de devolución:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setReturnType('partial')}
                    className={`rounded-lg border-2 p-3 transition-all ${
                      returnType === 'partial'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="text-sm font-medium">Parcial</p>
                    <p className="text-xs text-slate-600">
                      Seleccionar productos
                    </p>
                  </button>
                  <button
                    onClick={() => setReturnType('total')}
                    className={`rounded-lg border-2 p-3 transition-all ${
                      returnType === 'total'
                        ? 'border-red-500 bg-red-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="text-sm font-medium">Total</p>
                    <p className="text-xs text-slate-600">
                      Todos los productos
                    </p>
                  </button>
                </div>
              </div>

              {/* Items Selection (Partial Return) */}
              {returnType === 'partial' && (
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Selecciona los productos a devolver:
                  </label>
                  <div className="divide-y divide-slate-200 rounded-lg border border-slate-200">
                    {selectedSale.items.map((item) => {
                      const availableQty = item.quantity - item.returned
                      if (availableQty === 0) return null

                      return (
                        <div key={item.id} className="p-3">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`item-${item.id}`}
                              checked={selectedItems.includes(item.id)}
                              onCheckedChange={() =>
                                toggleItemSelection(item.id, availableQty)
                              }
                            />
                            <div className="flex-1">
                              <label
                                htmlFor={`item-${item.id}`}
                                className="cursor-pointer text-sm font-medium text-slate-900"
                              >
                                {item.productName}
                              </label>
                              <p className="mt-1 text-xs text-slate-600">
                                Precio: ${item.price.toFixed(2)} | Disponible:{' '}
                                {availableQty} uds
                              </p>
                              {selectedItems.includes(item.id) && (
                                <div className="mt-2 flex items-center gap-2">
                                  <label className="text-xs text-slate-600">
                                    Cantidad:
                                  </label>
                                  <Input
                                    type="number"
                                    min={1}
                                    max={availableQty}
                                    value={
                                      itemQuantities[item.id] || availableQty
                                    }
                                    onChange={(e) =>
                                      updateItemQuantity(
                                        item.id,
                                        Math.min(
                                          parseInt(e.target.value) || 1,
                                          availableQty,
                                        ),
                                      )
                                    }
                                    className="h-8 w-20 text-sm"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Return Reason */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Motivo de la devolución:
                </label>
                <select
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2"
                >
                  <option value="">Seleccionar motivo...</option>
                  <option value="Producto en mal estado">
                    Producto en mal estado
                  </option>
                  <option value="Producto equivocado">
                    Producto equivocado
                  </option>
                  <option value="Cliente no requiere el producto">
                    Cliente no requiere el producto
                  </option>
                  <option value="Reacción adversa">Reacción adversa</option>
                  <option value="Precio incorrecto">Precio incorrecto</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              {/* Return Total */}
              <div className="rounded-lg bg-blue-50 p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-blue-900">
                    Total a reembolsar:
                  </span>
                  <span className="text-2xl font-semibold text-blue-900">
                    ${calculateReturnTotal().toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={processReturn}
                  disabled={
                    !returnReason ||
                    (returnType === 'partial' && selectedItems.length === 0)
                  }
                  className="flex-1"
                >
                  Procesar Devolución
                </Button>
                <Button
                  onClick={() => setShowReturnModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
